import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { type AppDispatch, type RootState } from '@/presentation/store';
import { setInitializing, setUser } from '@/presentation/store/slices/auth/authSlice';
import { SyncSecureStorageUseCase } from '@/domain/usecases/sync/syncSecureStorageUseCase';
import { GetUserPremiumPackagesUseCase } from '@/domain/usecases/premiumPackage/getUserPremiumPackagesUseCase';
import { setUserPremiumPackages } from '@/presentation/store/slices/premiumPackages/premiumPackagesSlice';
import { GetWalletUseCase } from '@/domain/usecases/wallet/getWalletUseCase';
import { setWallet } from '@/presentation/store/slices/wallet/walletSlice';
import { initializeSecureStorageUseCase } from '@/domain/usecases/secureStorage/initializeSecureStorageUseCase';
import { fetchCurrentUserFromLocalStorageUseCase } from '@/domain/usecases/user/fetchCurrentUserFromLocalStorageUseCase';
import { refreshAuthUserUseCase } from '@/domain/usecases/user/refreshAuthUserUseCase';
import { showAccountSuspendedOverlay } from '@/presentation/store/slices/appOverlay/appOverlaySlice';
import { appLogger } from '@/domain/services/analytics/appLogger';

/**
 * AppInitializer
 *
 * Handles app initialization on startup for already-authenticated users.
 * This runs ONCE on app mount when auth state loading completes.
 *
 * Flow:
 * 1. Initialize SecureStorage (encryption setup with Firebase user ID)
 * 2. Load full user entity from SecureStorage (UserStorage → UserEntity)
 * 3. Populate Redux cache with user entity
 * 4. Refresh the user from the server and raise the account-suspended overlay
 *    when the refreshed user is suspended
 * 5. Trigger sync with full user entity (needed for blocked users, etc.)
 *
 * Note: useEffect dependency [isLoading] ensures this runs only once when
 * initial auth check completes. It does NOT re-run on firebaseUserId changes.
 *
 * Analytics user-id provider and global error handlers are registered in
 * `main.tsx` before React mounts so startup errors are also captured.
 */
export const AppInitializer = () => {
  const dispatch = useDispatch<AppDispatch>();
  const firebaseUserId = useSelector((state: RootState) => state.auth.firebaseUserId);
  const isLoading = useSelector((state: RootState) => state.auth.isLoading);

  useEffect(() => {
    // Wait for auth state to load
    if (isLoading) {
      return;
    }

    // No user - nothing to initialize. Flip the gate so ProtectedRoute can
    // proceed to its login redirect.
    if (!firebaseUserId) {
      dispatch(setInitializing(false));
      return;
    }

    // User exists on app startup - this is app restart scenario
    const initializeAppOnStartup = async () => {
      try {
        // console.log('[AppInitializer] App restart detected - initializing for user:', firebaseUserId);
        dispatch(setInitializing(true));

        // Step 1: Initialize SecureStorage with Firebase user ID
        await initializeSecureStorageUseCase.execute(firebaseUserId);
        // console.log('[AppInitializer] SecureStorage initialized');

        // Step 2: Load full user entity from SecureStorage (UserStorage → UserEntity)
        const userEntity = await fetchCurrentUserFromLocalStorageUseCase.execute();
        if (!userEntity) {
          appLogger.warning('No user found in storage', 'AppInitializer.initializeAppOnStartup');
          dispatch(setInitializing(false));
          return;
        }

        // Step 3: Populate Redux cache with cached user entity
        dispatch(setUser(userEntity));
        // console.log('[AppInitializer] User loaded to Redux cache:', userEntity.firebaseId);

        // Step 4: Refresh user from server so sync uses up-to-date data (e.g. blockedUsers).
        // The storage write inside refreshAuthUserUseCase emits on eventBus, which
        // AuthUserListener picks up and dispatches setUser — no manual dispatch here.
        // The use case logs and returns null on failure, so a refresh error doesn't
        // block subsequent sync/premium/wallet steps.
        let userForSync = userEntity;
        const updatedUser = await refreshAuthUserUseCase.execute(firebaseUserId);
        if (updatedUser) userForSync = updatedUser;

        // Flutter `MainScreen._refreshUser` raises the account-suspended overlay
        // right after the server read of the current user
        // (lib/presentation/screens/main/main_screen.dart:300-304). This is the
        // trigger for an account suspended mid-session; `LoginPage.onSuspended`
        // only covers a fresh sign-in.
        if (updatedUser?.suspended) {
          dispatch(showAccountSuspendedOverlay());
        }

        // Step 5: Trigger sync with full user entity
        // syncSecureStorageUseCase needs UserEntity (fetchUser already returns entity)
        const syncSecureStorageUseCase = new SyncSecureStorageUseCase();
        await syncSecureStorageUseCase.execute(userForSync);
        // console.log('[AppInitializer] Sync completed');

        // Step 6: Load user premium packages into Redux for synchronous access
        const getUserPremiumPackagesUseCase = new GetUserPremiumPackagesUseCase();
        const userPremiumPackages = await getUserPremiumPackagesUseCase.execute();
        dispatch(setUserPremiumPackages(userPremiumPackages));

        // Step 7: Load wallet into Redux for synchronous access
        const getWalletUseCase = new GetWalletUseCase();
        const wallet = await getWalletUseCase.execute();
        dispatch(setWallet(wallet));

        dispatch(setInitializing(false));
        // console.log('[AppInitializer] Initialization complete');
      } catch (error) {
        appLogger.error('Failed to initialize', 'AppInitializer.initializeAppOnStartup', { error: error as Error });
        dispatch(setInitializing(false));
      }
    };

    initializeAppOnStartup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]); // Only run when isLoading changes (once on mount)

  // This component doesn't render anything
  return null;
};